import React, { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  AppsOutlined,
  ExpandMore,
  PointOfSaleOutlined,
  PushPin,
  PushPinOutlined,
  SellOutlined,
} from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { setActiveTabByPath } from "../../tabs/tabsSlice";
import { useAuth } from "../../context/AuthContext";
import { canAccessPermissionEntry } from "../../config/permissionUtils";
import { removeSalesDraft } from "../../state/salesDraftsSlice";
import { buildSalesDraftPath, getSalesDraftStorageKey } from "../../state/salesDraftUtils";

const COLLAPSED_W = 65;
const HOVER_W = 235;
const EXPANDED_W = 240;
const APPBAR_H = 64;
const DRAWER_SIDE_GAP = 10;
const DRAWER_TOP_GAP = 6;
const DRAWER_BOTTOM_GAP = 10;
const DRAWER_TOP_OFFSET = APPBAR_H + DRAWER_TOP_GAP;
const DRAWER_TRANSITION = "width 180ms cubic-bezier(0.2,0,0,1)";
const SIDEBAR_SALES_DRAFT_ID = "venta-normal";

const asArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value == null) return [];
  return [value];
};

const hasVisibleLabel = (entry) =>
  typeof entry?.label === "string" && entry.label.trim().length > 0;

const hasVisibleNavText = (entry) => {
  if (hasVisibleLabel(entry)) return true;
  return typeof entry?.title === "string" && entry.title.trim().length > 0;
};

const isSidebarVisible = (entry) =>
  Boolean(entry) && entry.sidebar !== false && entry.hidden !== true;

const isNavVisible = (entry) => Boolean(entry) && entry.nav === true;

const normalizeSection = (section, fallbackPrefix = "section") => {
  if (!section || typeof section !== "object") return null;
  const rawItems = section.items || section.subitems || [];
  const items = asArray(rawItems).filter((item) => item && typeof item === "object");
  return {
    id: section.id || section.key || section.code || `${fallbackPrefix}-${section.title || section.label || "item"}`,
    title: section.title || section.label || section.name || "Seccion",
    icon: section.icon || <AppsOutlined fontSize="small" />,
    path: section.path || null,
    requiredPerms: section.requiredPerms || section.perms || section.permissions || null,
    requiredAnyPerms: section.requiredAnyPerms || section.anyPerms || section.permissionsAny || null,
    requiresCashierQueue: section.requiresCashierQueue || false,
    requiresBilling: section.requiresBilling || false,
    requiresDeferredBilling: section.requiresDeferredBilling || false,
    nav: section.nav,
    sidebar: section.sidebar,
    hidden: section.hidden,
    items,
  };
};

const normalizePath = (path) => {
  if (!path || typeof path !== "string") return "";
  if (path === "/") return "/";
  return path.endsWith("/") ? path.slice(0, -1) : path;
};

const hasUsableNavTarget = (entry) => Boolean(normalizePath(entry?.path));

const mergeSectionsById = (sectionsList) => {
  const byId = new Map();
  sectionsList.forEach((section) => {
    const id = section?.id || section?.title;
    if (!id) return;
    const current = byId.get(id);
    if (!current) {
      byId.set(id, { ...section, items: asArray(section.items) });
      return;
    }
    const seenItems = new Set(
      asArray(current.items).map((item) => item?.id || item?.path || item?.label).filter(Boolean)
    );
    const mergedItems = [...asArray(current.items)];
    asArray(section.items).forEach((item) => {
      const itemKey = item?.id || item?.path || item?.label;
      if (!itemKey || seenItems.has(itemKey)) return;
      seenItems.add(itemKey);
      mergedItems.push(item);
    });
    byId.set(id, {
      ...current,
      path: current.path || section.path || null,
      items: mergedItems,
    });
  });
  return Array.from(byId.values());
};

function SlideBar({
  drawerOpen = false,
  setDrawerOpen,
  pinned = false,
  setPinned,
  expanded = null,
  setExpanded,
  navRoutes = [],
  adminSections = [],
  businessSections = [],
}) {
  const isMobile = useMediaQuery("(max-width:768px)");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const theme = useTheme();
  const { effectivePerms = [], user, salesConfig } = useAuth();
  const [hoverOpen, setHoverOpen] = useState(false);

  const open = Boolean(isMobile ? drawerOpen : pinned || hoverOpen);
  const computedW = pinned ? EXPANDED_W : open ? HOVER_W : COLLAPSED_W;

  // ─── Colores del sidebar derivados del theme ───────────────────────────────
  const BG         = theme.palette.primary.dark;
  const ACTIVE_BG  = alpha(theme.palette.common.white, 0.14);
  const HOVER_BG   = alpha(theme.palette.common.white, 0.08);
  const DIVIDER_C  = alpha(theme.palette.common.white, 0.12);
  const TEXT_C     = theme.palette.primary.contrastText;
  const ACCENT     = theme.palette.secondary.main;

  const handleMouseEnter = () => { if (isMobile || pinned) return; setHoverOpen(true); };
  const handleMouseLeave = () => {
    if (isMobile || pinned) return;
    setHoverOpen(false);
    if (typeof setExpanded === "function") setExpanded(null);
  };

  const isActive = (path) => {
    const target = normalizePath(path);
    if (!target) return false;
    const current = normalizePath(location?.pathname || "/");
    if (target === "/") return current === "/" || current === "/home";
    if (target === "/home") return current === "/home" || current === "/";
    return current === target || current.startsWith(`${target}/`);
  };

  const hasPerm = (entry) =>
    canAccessPermissionEntry(effectivePerms, entry, user?.is_superuser, salesConfig);
  const isHomePath = (path) => {
    const n = normalizePath(path);
    return n === "/" || n === "/home";
  };

  const normalizedAdminSections = asArray(adminSections)
    .map((s, i) => normalizeSection(s, `admin-${i}`)).filter(Boolean);
  const normalizedBusinessSections = asArray(businessSections)
    .map((s, i) => normalizeSection(s, `business-${i}`)).filter(Boolean);

  const operationalEntries = asArray(navRoutes)
    .filter((r) => r && typeof r === "object")
    .filter(isNavVisible).filter(isSidebarVisible).filter(hasVisibleLabel).filter(hasUsableNavTarget)
    .map((r) => ({
      id: r.id || r.path || r.label,
      label: r.label,
      path: r.path || null,
      icon: r.icon || <AppsOutlined fontSize="small" />,
      requiredPerms: r.requiredPerms || r.perms || r.permissions || null,
      requiredAnyPerms: r.requiredAnyPerms || r.anyPerms || r.permissionsAny || null,
      requiresCashierQueue: r.requiresCashierQueue || false,
      requiresBilling: r.requiresBilling || false,
      requiresDeferredBilling: r.requiresDeferredBilling || false,
      salesDraftAction: r.salesDraftAction || null,
      nav: r.nav, sidebar: r.sidebar, hidden: r.hidden,
    }))
    .filter(hasPerm);

  const quickAccessEntries = [
    {
      id: "quick-sale",
      label: "Nueva venta",
      path: "/sales",
      icon: <SellOutlined fontSize="small" />,
      requiredAnyPerms: ["sale_create"],
      nav: true,
      sidebar: true,
      salesDraftAction: "new",
    },
    {
      id: "quick-cashbox",
      label: "Caja",
      path: "/cashbox",
      icon: <PointOfSaleOutlined fontSize="small" />,
      requiredPerms: ["cash_view"],
      requiresCashierQueue: true,
      nav: true,
      sidebar: true,
    },
  ].filter(hasPerm);

  const providedSections = mergeSectionsById([...normalizedBusinessSections, ...normalizedAdminSections])
    .map((section) => ({
      ...section,
      items: asArray(section.items).filter(
        (item) =>
          isNavVisible(item) &&
          isSidebarVisible(item) &&
          hasVisibleLabel(item) &&
          hasUsableNavTarget(item) &&
          hasPerm(item)
      ),
    }))
    .filter(isNavVisible).filter(isSidebarVisible).filter(hasVisibleNavText).filter(hasPerm)
    .filter((s) => Boolean(s.path) || s.items.length > 0);

  const sections = providedSections;
  const homeOperationalEntries = operationalEntries.filter((entry) => isHomePath(entry?.path));
  const secondaryOperationalEntries = operationalEntries.filter((entry) => !isHomePath(entry?.path));
  const expandedOperationalEntries = [
    ...homeOperationalEntries,
    ...quickAccessEntries,
    ...secondaryOperationalEntries,
  ];
  const collapsedEntries = [...expandedOperationalEntries, ...sections];

  const closeMobileDrawer = () => {
    if (typeof setDrawerOpen === "function") setDrawerOpen(false);
  };

  const blurCurrentTarget = (event) => {
    if (event?.currentTarget?.blur) event.currentTarget.blur();
    if (document.activeElement?.blur) document.activeElement.blur();
  };

  const handleItemClick = (event, item) => {
    blurCurrentTarget(event);
    if (item?.salesDraftAction === "new") {
      const draftPath = buildSalesDraftPath(SIDEBAR_SALES_DRAFT_ID);
      dispatch(removeSalesDraft(getSalesDraftStorageKey(`?draft=${SIDEBAR_SALES_DRAFT_ID}`)));
      navigate(draftPath);
      if (isMobile) closeMobileDrawer();
      return;
    }
    if (item?.path) {
      if (isHomePath(item.path)) dispatch(setActiveTabByPath(null));
      navigate(item.path);
      if (isMobile) closeMobileDrawer();
    }
  };

  const togglePinned = () => {
    setHoverOpen(false);
    if (typeof setPinned === "function") setPinned((prev) => !prev);
    if (typeof setExpanded === "function") setExpanded(null);
  };

  // ─── Estilos de ítem ────────────────────────────────────────────────────────
  const getItemSx = (active, collapsed = false) => ({
    borderRadius: 1.5,
    minHeight: collapsed ? 44 : 38,
    mb: 0.5,
    px: collapsed ? 0 : 1.25,
    justifyContent: collapsed ? "center" : "flex-start",
    position: "relative",
    overflow: "hidden",
    bgcolor: active ? ACTIVE_BG : "transparent",
    // Accent bar izquierdo en ítems activos
    "&::before": active && !collapsed ? {
      content: '""',
      position: "absolute",
      left: 0, top: "20%", bottom: "20%",
      width: 3,
      borderRadius: "0 3px 3px 0",
      bgcolor: ACCENT,
    } : {},
    "&:hover": { bgcolor: active ? ACTIVE_BG : HOVER_BG },
    "&:active": { bgcolor: alpha(theme.palette.common.white, 0.18) },
    "&:focus, &:focus-visible, &.Mui-focusVisible": { outline: "none", boxShadow: "none" },
    transition: "background-color 0.15s ease",
  });

  // ─── Render collapsed ───────────────────────────────────────────────────────
  const renderCollapsedList = () => (
    <List sx={{ px: 0.75, py: 1 }}>
      {collapsedEntries
        .filter((e) => isNavVisible(e) && isSidebarVisible(e) && hasVisibleNavText(e))
        .map((entry) => {
          const active = entry?.path ? isActive(entry.path) : false;
          return (
            <Tooltip
              key={entry.id || entry.path || entry.label || entry.title}
              title={entry.label || entry.title || ""}
              placement="right"
              arrow
            >
              <ListItemButton onClick={(e) => handleItemClick(e, entry)} sx={getItemSx(active, true)}>
                <ListItemIcon sx={{
                  minWidth: 0,
                  color: active ? TEXT_C : alpha(TEXT_C, 0.65),
                  transition: "color 0.15s",
                }}>
                  {entry.icon || <AppsOutlined fontSize="small" />}
                </ListItemIcon>
              </ListItemButton>
            </Tooltip>
          );
        })}
    </List>
  );

  // ─── Render expanded ────────────────────────────────────────────────────────
  const renderExpandedList = () => (
    <List sx={{ px: 0.75, py: 1 }}>
      {expandedOperationalEntries.map((entry) => {
        const active = isActive(entry.path);
        return (
          <ListItemButton
            key={entry.id || entry.path || entry.label}
            onClick={(e) => handleItemClick(e, entry)}
            sx={{ ...getItemSx(active, false), mb: 0.5 }}
          >
            <ListItemIcon sx={{
              minWidth: 32,
              color: active ? TEXT_C : alpha(TEXT_C, 0.6),
              transition: "color 0.15s",
            }}>
              {entry.icon || <AppsOutlined fontSize="small" />}
            </ListItemIcon>
            <ListItemText
              primary={entry.label || "Item"}
              primaryTypographyProps={{
                fontSize: 13.5,
                fontWeight: active ? 800 : 600,
                color: active ? TEXT_C : alpha(TEXT_C, 0.8),
                letterSpacing: "0.01em",
              }}
            />
          </ListItemButton>
        );
      })}

      {expandedOperationalEntries.length > 0 && sections.length > 0 && (
        <Box sx={{ px: 1.25, py: 1 }}>
          <Divider sx={{ borderColor: DIVIDER_C }} />
        </Box>
      )}

      {sections.map((section) => {
        const hasSubitems = Array.isArray(section.items) && section.items.length > 0;

        if (!hasSubitems) {
          const active = section?.path ? isActive(section.path) : false;
          return (
            <ListItemButton
              key={section.id}
              onClick={(e) => handleItemClick(e, section)}
              sx={{ ...getItemSx(active, false), mb: 0.5 }}
            >
              <ListItemIcon sx={{
                minWidth: 32,
                color: active ? TEXT_C : alpha(TEXT_C, 0.6),
              }}>
                {section.icon || <AppsOutlined fontSize="small" />}
              </ListItemIcon>
              <ListItemText
                primary={section.title}
                primaryTypographyProps={{
                  fontSize: 13.5,
                  fontWeight: active ? 800 : 600,
                  color: active ? TEXT_C : alpha(TEXT_C, 0.8),
                }}
              />
            </ListItemButton>
          );
        }

        const sectionHasActiveItem = section.items.some((item) => item?.path && isActive(item.path));

        return (
          <Accordion
            key={section.id}
            disableGutters
            square={false}
            expanded={open && expanded === section.id}
            onChange={(_, isExpanded) => {
              if (!open) return;
              if (typeof setExpanded === "function") setExpanded(isExpanded ? section.id : null);
            }}
            sx={{
              bgcolor: "transparent",
              color: TEXT_C,
              boxShadow: "none",
              "&:before": { display: "none" },
            }}
          >
            <AccordionSummary
              expandIcon={open ? <ExpandMore sx={{ color: alpha(TEXT_C, 0.6), fontSize: 18 }} /> : null}
              onClick={blurCurrentTarget}
              sx={{
                minHeight: 38,
                px: 1.25,
                borderRadius: 1.5,
                mb: 0.25,
                bgcolor: sectionHasActiveItem ? ACTIVE_BG : "transparent",
                "& .MuiAccordionSummary-content": { my: 0.4 },
                "&:hover": { bgcolor: HOVER_BG },
                "&:focus, &:focus-visible, &.Mui-focusVisible": { outline: "none", boxShadow: "none" },
              }}
            >
              <ListItemIcon sx={{ minWidth: 28, color: sectionHasActiveItem ? TEXT_C : alpha(TEXT_C, 0.6) }}>
                {section.icon || <AppsOutlined fontSize="small" />}
              </ListItemIcon>
              <Typography sx={{
                fontWeight: 700,
                fontSize: 13,
                color: sectionHasActiveItem ? TEXT_C : alpha(TEXT_C, 0.75),
                letterSpacing: "0.02em",
              }}>
                {section.title}
              </Typography>
            </AccordionSummary>

            <AccordionDetails sx={{ pt: 0, pb: 0.5, px: 0.5 }}>
              {/* Línea vertical de conexión */}
              <Box sx={{ position: "relative", pl: 1.5 }}>
                <Box sx={{
                  position: "absolute",
                  left: "1.1rem",
                  top: 0, bottom: 8,
                  width: 1.5,
                  bgcolor: DIVIDER_C,
                  borderRadius: 1,
                }} />
                {(section.items || []).map((item) => {
                  const active = item?.path ? isActive(item.path) : false;
                  return (
                    <ListItemButton
                      key={item.id || item.path || item.label}
                      onClick={(e) => handleItemClick(e, item)}
                      sx={{
                        ...getItemSx(active, false),
                        pl: 2.5,
                        minHeight: 34,
                        mb: 0.25,
                      }}
                    >
                      <ListItemIcon sx={{
                        minWidth: 28,
                        color: active ? TEXT_C : alpha(TEXT_C, 0.55),
                      }}>
                        {item.icon || <AppsOutlined fontSize="small" />}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.label || "Item"}
                        primaryTypographyProps={{
                          fontSize: 13,
                          fontWeight: active ? 700 : 500,
                          color: active ? TEXT_C : alpha(TEXT_C, 0.72),
                        }}
                      />
                    </ListItemButton>
                  );
                })}
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </List>
  );

  // ─── Drawer body ────────────────────────────────────────────────────────────
  const drawerBody = (
    <Box sx={{ height: "100%", minHeight: 0, display: "flex", flexDirection: "column", bgcolor: BG }}>
      {/* Textura de puntos sutil */}
      <Box sx={{
        position: "absolute",
        inset: 0,
        backgroundImage: `radial-gradient(circle, ${alpha(theme.palette.common.white, 0.04)} 1px, transparent 1px)`,
        backgroundSize: "20px 20px",
        pointerEvents: "none",
        zIndex: 0,
      }} />

      <Toolbar
        disableGutters
        sx={{
          position: "relative",
          zIndex: 1,
          minHeight: 56,
          px: open ? 1.5 : 0.75,
          display: "flex",
          alignItems: "center",
          justifyContent: open ? "flex-end" : "center",
        }}
      >
        <Tooltip title={pinned ? "Desanclar menu" : "Anclar menu"} placement="right">
          <IconButton
            onClick={togglePinned}
            size="small"
            sx={{
              color: alpha(TEXT_C, 0.5),
              "&:hover": { color: TEXT_C, bgcolor: HOVER_BG },
              transition: "all 0.2s",
            }}
          >
            {pinned
              ? <PushPin fontSize="small" />
              : <PushPinOutlined fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Toolbar>

      <Divider sx={{ borderColor: DIVIDER_C, position: "relative", zIndex: 1 }} />

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overscrollBehavior: "contain",
          position: "relative",
          zIndex: 1,
          "&::-webkit-scrollbar": { display: "none" },
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        {open || isMobile ? renderExpandedList() : renderCollapsedList()}
      </Box>

      <Divider sx={{ borderColor: DIVIDER_C, position: "relative", zIndex: 1 }} />

      {/* Footer */}
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          px: open ? 1.5 : 0.75,
          py: 1.25,
          display: "flex",
          alignItems: "center",
          justifyContent: open ? "flex-start" : "center",
          gap: 1,
        }}
      >
        {open ? (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box sx={{
              width: 28, height: 28,
              borderRadius: 1,
              bgcolor: ACCENT,
              display: "grid",
              placeItems: "center",
              fontWeight: 900,
              fontSize: 11,
              color: theme.palette.secondary.contrastText,
              letterSpacing: "0.05em",
              flexShrink: 0,
            }}>
              PP
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: 12.5, color: TEXT_C, lineHeight: 1.2 }}>
                Pawsi Patitas
              </Typography>
              <Typography sx={{ fontSize: 10.5, color: alpha(TEXT_C, 0.45), lineHeight: 1 }}>
                © {new Date().getFullYear()}
              </Typography>
            </Box>
          </Stack>
        ) : (
          <Box sx={{
            width: 28, height: 28,
            borderRadius: 1,
            bgcolor: ACCENT,
            display: "grid",
            placeItems: "center",
            fontWeight: 900,
            fontSize: 11,
            color: theme.palette.secondary.contrastText,
            letterSpacing: "0.05em",
          }}>
            PP
          </Box>
        )}
      </Box>
    </Box>
  );

  // ─── Estilos compartidos del Drawer paper ──────────────────────────────────
  const paperSx = {
    bgcolor: BG,
    color: TEXT_C,
    border: "none",
    boxShadow: `4px 0 24px ${alpha(theme.palette.common.black, 0.18)}`,
    borderRadius: "10px",
    overflow: "hidden",
  };

  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={Boolean(drawerOpen)}
        onClose={closeMobileDrawer}
        ModalProps={{ keepMounted: true }}
        sx={{ "& .MuiDrawer-paper": { width: EXPANDED_W, ...paperSx } }}
      >
        {drawerBody}
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="permanent"
      open
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        width: 0,
        flex: "0 0 0px",
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: computedW,
          height: `calc(100vh - ${DRAWER_TOP_OFFSET + DRAWER_BOTTOM_GAP}px)`,
          position: "fixed",
          top: `${DRAWER_TOP_OFFSET}px`,
          left: `${DRAWER_SIDE_GAP}px`,
          zIndex: 1200,
          boxSizing: "border-box",
          overflowX: "hidden",
          transition: DRAWER_TRANSITION,
          willChange: "width",
          transform: "translateZ(0)",
          ...paperSx,
        },
      }}
    >
      {drawerBody}
    </Drawer>
  );
}

// Stack import faltaba

export { COLLAPSED_W, HOVER_W, EXPANDED_W };
export default SlideBar;
